import { AccessComposableDocument } from "../services/graphql/generated/consumer-graph-types";
import { hasActionAndResourceAccess, hasFeatureAccess } from "@/shared/access-control/composables/use-has-access.ts";
import { AccessAction, AccessFeature, AccessResource } from "@/shared/access-control/access-control.ts";
import { consumerClient } from "@/shared/services/apollo-clients/consumer-client.ts";

/**
 * Async (non-reactive) variant of {@link useAiAssistantAccess} for use outside Vue setup context
 * (e.g. ability `init()`, router guards).
 *
 * Applies the identical access rules by querying Apollo directly:
 * 1. `rag_rbac` disabled → always granted
 * 2. Anonymous user     → requires `rag_anonymous` feature
 * 3. `rag` resource present → requires `rag:write` resource permission
 * 4. Otherwise           → denied
 */
export const useAiAssistantAccessAsync = async (): Promise<boolean> => {
    const { data } = await consumerClient.query({
        query: AccessComposableDocument,
        fetchPolicy: "cache-first",
    });

    const hasRagGui = hasFeatureAccess(AccessFeature.rag_gui, data);
    if (!hasRagGui) return false;

    const hasRagRbac = hasFeatureAccess(AccessFeature.rag_rbac, data);
    if (!hasRagRbac) return true;

    const isAnonymous = !!data?.me?.anonymous;
    if (isAnonymous) return hasFeatureAccess(AccessFeature.rag_anonymous, data);

    const hasRagResource = hasFeatureAccess(AccessResource.rag, data);
    if (hasRagResource) return hasActionAndResourceAccess(data, data, AccessAction.write, AccessResource.rag);

    return false;
};
